import { toYMD } from '../utils/date';

// Raw API response type (matches current backend structure)
export type ApiProductionOrderWithProduct = {
  production_order_id: number;
  product_id: number;
  quantity_cases: number;
  quantity_units: number;
  due_date?: string;
  priority_level: 'low' | 'normal' | 'high' | 'urgent';
  status: 'pending' | 'in_progress' | 'completed' | 'cancelled';
  estimated_production_hours?: number; // Average based on 6 workers baseline
  notes?: string;
  created_by?: number;
  created_at: string;
  updated_at: string;
  product_name: string;
  product_code: string;
  units_per_case: number;
  packing_speed_units: number;
  packing_speed_time: number;
  packing_speed_unit: string;
  production_line_id?: number;
  production_line_name?: string;
  line_type?: 'manual' | 'machine';
  product_production_line_id?: number;
  planned_production_date?: string | null; // From schedule entries
  planned_production_time?: string | null; // From schedule entries
  is_split_order?: boolean; // Indicates if order is split into multiple portions
  schedule_portions?: Array<{
    portion_number: number;
    scheduled_date: string;
    scheduled_time: string;
    duration_minutes: number;
    estimated_production_minutes?: number;
    production_line_id?: number;
    production_line_name?: string;
  }>; // Array of all schedule portions
};

// Normalized UI type for Gantt chart
export type UiOrder = {
  production_order_id: number;
  product_name: string;
  product_code: string;
  status: string;
  priority_level: string;
  due_date: string | null; // 'YYYY-MM-DD' or null
  estimated_production_hours: number | null; // Average based on 6 workers baseline
  quantity_cases: number;
  quantity_units: number;
  notes?: string;
  created_at: string;
  updated_at: string;
  production_line_id?: number;
  production_line_name?: string;
  line_type?: 'manual' | 'machine';
  product_production_line_id?: number;
  planned_production_date?: string | null; // From schedule entries
  planned_production_time?: string | null; // From schedule entries
  is_split_order?: boolean; // Indicates if order is split into multiple portions
  schedule_portions?: Array<{
    portion_number: number;
    scheduled_date: string;
    scheduled_time: string;
    duration_minutes: number;
    estimated_production_minutes?: number;
    production_line_id?: number;
    production_line_name?: string;
  }>; // Array of all schedule portions
  // Legacy break-spanning properties (for migration)
  pre_break_minutes?: number;
  post_break_minutes?: number;
  break_pause_start?: string;
  break_pause_end?: string;
};

export function mapToUiOrder(apiOrder: ApiProductionOrderWithProduct): UiOrder {
  const mapped = {
    production_order_id: apiOrder.production_order_id,
    product_name: apiOrder.product_name ?? 'Unknown Product',
    product_code: apiOrder.product_code ?? '',
    status: apiOrder.status ?? 'pending',
    priority_level: apiOrder.priority_level ?? 'normal',
    due_date: toYMD(apiOrder.due_date ?? null),
    estimated_production_hours: apiOrder.estimated_production_hours ?? null,
    quantity_cases: apiOrder.quantity_cases ?? 0,
    quantity_units: apiOrder.quantity_units ?? 0,
    notes: apiOrder.notes,
    created_at: apiOrder.created_at,
    updated_at: apiOrder.updated_at,
    production_line_id: apiOrder.production_line_id ?? undefined,
    production_line_name: apiOrder.production_line_name ?? undefined,
    line_type: apiOrder.line_type ?? undefined,
    product_production_line_id: (apiOrder as any).product_production_line_id ?? undefined,
    planned_production_date: apiOrder.planned_production_date ?? null,
    planned_production_time: apiOrder.planned_production_time ?? null,
    is_split_order: (apiOrder as any).is_split_order ?? false,
    schedule_portions: (apiOrder as any).schedule_portions ?? undefined,
    // Legacy break-spanning properties (for migration)
    pre_break_minutes: (apiOrder as any).pre_break_minutes ?? undefined,
    post_break_minutes: (apiOrder as any).post_break_minutes ?? undefined,
    break_pause_start: (apiOrder as any).break_pause_start ?? undefined,
    break_pause_end: (apiOrder as any).break_pause_end ?? undefined,
  };

  return mapped;
}
